import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import DocumentHeader from '../../../Documents/components/DocumentHeader';
import OriginalPreview from './OriginalPreview';
import NewPreview from './NewPreview';
import CopyReplaceConfirmModal from './CopyReplaceConfirmModal';
import Loader from '../../../../common/Loader';
import { getProcedureRevisionById } from '../../../../redux/procedureRevisionSlice';

/**
 * CopyReplacePreview Component
 * Displays the preview of original vs new document state
 * Data fetching is handled in CopyReplace.js:
 * - Original view: shows data fetched from target document's revision ID (getRevAllNodes)
 * - New view: shows data fetched from source document's revision ID (getRevAllNodes)
 */
const CopyReplacePreview = ({
  sourceDocument,
  sourceNode,
  copyMode,
  targetDocument,
  pastePosition,
  previewMode,
  originalData,
  newData,
  onPreviewModeChange,
  onConfirm,
  comment,
  onCommentChange,
  isLoading,
  isModalOpen,
  onModalClose
}) => {
  const dispatch = useDispatch();
  const [revisionStatus, setRevisionStatus] = useState('Not Submitted');

  // Fetch revision status
  useEffect(() => {
    const fetchRevisionStatus = async () => {
      if (targetDocument && targetDocument.revisionId) {
        try {
          const result = await dispatch(getProcedureRevisionById(targetDocument.revisionId)).unwrap();
          const revisionData = result?.results || result;
          if (revisionData) {
            // Extract status from status_changes
            const latestStatus = revisionData?.status_changes?.find(sc => sc.is_latest)?.status_changed_to;
            const currentStatus = latestStatus || 'Not Submitted';
            setRevisionStatus(currentStatus);
          }
        } catch (error) {
          console.error('Error fetching revision status:', error);
        }
      }
    };

    fetchRevisionStatus();
  }, [targetDocument, dispatch]);

  const handleModalSave = () => {
    if (onConfirm) {
      onConfirm();
    }
    if (onModalClose) {
      onModalClose();
    }
  };

  return (
    <div style={{ padding: '20px', position: 'relative' }}>
      {/* Document Header - Show target document header above preview */}
      {targetDocument && (
        <DocumentHeader
          type={targetDocument.type}
          partNumber={targetDocument.partNumber}
          revision={targetDocument.revision}
          revisionId={targetDocument.revisionId}
          partId={targetDocument.partId}
          // description={targetDocument.description}
          description={false}
          status={revisionStatus}
          currentRevision={null} // Not needed since showVersionInfo is false
          showCheckbox={false}
          showVersionInfo={false} // Hide based on text
          isCheckboxEnabled={false}
        />
      )}

      {/* Preview Content */}
      {previewMode === 'original' ? (
        <OriginalPreview
          data={originalData}
          targetDocument={targetDocument}
        />
      ) : (
        <NewPreview
          originalData={originalData}
          sourceData={sourceDocument}
          targetDocument={targetDocument}
          newData={newData}
        />
      )}

      {/* Confirmation Modal */}
      {isModalOpen && (
        <CopyReplaceConfirmModal
          sourceDocument={sourceDocument}
          sourceNode={sourceNode}
          copyMode={copyMode}
          targetDocument={targetDocument}
          pastePosition={pastePosition}
          comment={comment}
          onCommentChange={onCommentChange}
          onClose={onModalClose}
          onSave={handleModalSave}
          isLoading={isLoading}
        />
      )}

      {/* Submitting overlay loader */}
      {isLoading && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(255,255,255,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <Loader text="Copying" />
        </div>
      )}
    </div>
  );
};

export default CopyReplacePreview;